import type {QrType} from '../constants/qrTypes'

export type WifiEncryption = 'WPA' | 'WEP' | 'nopass'

export interface QrUrlFields {
  value: string
}
export interface QrTextFields {
  value: string
}
export interface QrPhoneFields {
  value: string
}
export interface QrEmailFields {
  email: string
  subject: string
  body: string
}
export interface QrSmsFields {
  phone: string
  message: string
}
export interface QrWhatsappFields {
  phone: string
  message: string
}
export interface QrWifiFields {
  ssid: string
  password: string
  encryption: WifiEncryption
  hidden: boolean
}
export interface QrVCardFields {
  firstName: string
  lastName: string
  org: string
  phone: string
  email: string
  website: string
}
export interface QrEventFields {
  title: string
  location: string
  description: string
  start: string
  end: string
}
export interface QrLocationFields {
  lat: string
  lng: string
}

export interface QrFieldsState {
  url: QrUrlFields
  text: QrTextFields
  email: QrEmailFields
  phone: QrPhoneFields
  sms: QrSmsFields
  whatsapp: QrWhatsappFields
  wifi: QrWifiFields
  vcard: QrVCardFields
  event: QrEventFields
  location: QrLocationFields
}

export const DEFAULT_QR_FIELDS: QrFieldsState = {
  url: {value: ''},
  text: {value: ''},
  email: {email: '', subject: '', body: ''},
  phone: {value: ''},
  sms: {phone: '', message: ''},
  whatsapp: {phone: '', message: ''},
  wifi: {ssid: '', password: '', encryption: 'WPA', hidden: false},
  vcard: {firstName: '', lastName: '', org: '', phone: '', email: '', website: ''},
  event: {title: '', location: '', description: '', start: '', end: ''},
  location: {lat: '', lng: ''},
}

const normalizeUrl = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

const escapeWifiValue = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/:/g, '\\:')

const buildWifiPayload = (f: QrWifiFields): string => {
  const ssid = f.ssid.trim()
  if (!ssid) return ''
  const parts = [`T:${f.encryption}`, `S:${escapeWifiValue(ssid)}`]
  if (f.encryption !== 'nopass' && f.password) parts.push(`P:${escapeWifiValue(f.password)}`)
  if (f.hidden) parts.push('H:true')
  return `WIFI:${parts.join(';')};;`
}

const buildMailtoPayload = (f: QrEmailFields): string => {
  const email = f.email.trim()
  if (!email) return ''
  const params = new URLSearchParams()
  if (f.subject) params.set('subject', f.subject)
  if (f.body) params.set('body', f.body)
  const qs = params.toString()
  return `mailto:${email}${qs ? `?${qs}` : ''}`
}

const buildSmsPayload = (f: QrSmsFields): string => {
  const phone = f.phone.trim()
  if (!phone) return ''
  const body = f.message ? `?body=${encodeURIComponent(f.message)}` : ''
  return `sms:${phone}${body}`
}

const buildWhatsappPayload = (f: QrWhatsappFields): string => {
  const digits = f.phone.replace(/[^0-9]/g, '')
  if (!digits) return ''
  const text = f.message ? `?text=${encodeURIComponent(f.message)}` : ''
  return `https://wa.me/${digits}${text}`
}

const buildVCardPayload = (f: QrVCardFields): string => {
  const firstName = f.firstName.trim()
  const lastName = f.lastName.trim()
  if (!firstName && !lastName) return ''
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${lastName};${firstName};;;`,
    `FN:${[firstName, lastName].filter(Boolean).join(' ')}`,
  ]
  if (f.org.trim()) lines.push(`ORG:${f.org.trim()}`)
  if (f.phone.trim()) lines.push(`TEL:${f.phone.trim()}`)
  if (f.email.trim()) lines.push(`EMAIL:${f.email.trim()}`)
  if (f.website.trim()) lines.push(`URL:${normalizeUrl(f.website)}`)
  lines.push('END:VCARD')
  return lines.join('\n')
}

// `datetime-local` input value is `YYYY-MM-DDTHH:mm` — strip separators and
// append seconds for the iCalendar floating-time format `YYYYMMDDTHHMMSS`.
const toIcsDate = (local: string): string => `${local.replace(/[-:]/g, '')}00`

const buildEventPayload = (f: QrEventFields): string => {
  const title = f.title.trim()
  if (!title || !f.start) return ''
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'BEGIN:VEVENT', `SUMMARY:${title}`, `DTSTART:${toIcsDate(f.start)}`]
  if (f.end) lines.push(`DTEND:${toIcsDate(f.end)}`)
  if (f.location.trim()) lines.push(`LOCATION:${f.location.trim()}`)
  if (f.description.trim()) lines.push(`DESCRIPTION:${f.description.trim()}`)
  lines.push('END:VEVENT', 'END:VCALENDAR')
  return lines.join('\n')
}

const buildLocationPayload = (f: QrLocationFields): string => {
  const lat = f.lat.trim()
  const lng = f.lng.trim()
  if (!lat || !lng) return ''
  return `geo:${lat},${lng}`
}

/** Builds the raw string that gets encoded into the QR code for the active type. */
export const buildQrPayload = (type: QrType, fields: QrFieldsState): string => {
  switch (type) {
    case 'url':
      return normalizeUrl(fields.url.value)
    case 'text':
      return fields.text.value
    case 'email':
      return buildMailtoPayload(fields.email)
    case 'phone':
      return fields.phone.value.trim() ? `tel:${fields.phone.value.trim()}` : ''
    case 'sms':
      return buildSmsPayload(fields.sms)
    case 'whatsapp':
      return buildWhatsappPayload(fields.whatsapp)
    case 'wifi':
      return buildWifiPayload(fields.wifi)
    case 'vcard':
      return buildVCardPayload(fields.vcard)
    case 'event':
      return buildEventPayload(fields.event)
    case 'location':
      return buildLocationPayload(fields.location)
  }
}
